import { Link } from "react-router-dom";
import { ArrowUpRight, Facebook, Instagram, Youtube, Leaf, MapPin, Phone } from "lucide-react";

const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_0fbf747e-f9ce-419f-bd04-07b0f1580b22/artifacts/snl5ovgh_logo02.png";

// Lucide has no dedicated X (formerly Twitter) mark. Custom inline SVG to match line-icon set.
const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.834L1.254 2.25H8.08l4.713 6.231L18.244 2.25Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);

const SOCIALS = [
  { Icon: Facebook,  href: "https://www.facebook.com/people/Tarzan/61581985721006/", label: "Facebook"  },
  { Icon: Instagram, href: "https://www.instagram.com/tarzanretreat/",                label: "Instagram" },
  { Icon: Youtube,   href: "https://youtube.com/@Tarzanretreat",                      label: "YouTube"   },
  { Icon: XIcon,     href: "https://x.com/tarzanretreat",                             label: "X"         },
];

const COLS = [
  { title: "Explore", links: [
    { label: "All Stays",     to: "/stays" },
    { label: "Experiences",   to: "/experiences" },
    { label: "Gallery",       to: "/gallery" },
    { label: "Gift Cards",    to: "/contact" },
  ]},
  { title: "Company", links: [
    { label: "About Us",      to: "/about" },
    { label: "Contact",       to: "/contact" },
    { label: "Sustainability",to: "/sustainability" },
    { label: "Partner With Us",to: "/partner" },
  ]},
  { title: "Support", links: [
    { label: "Help Centre",        to: "/help" },
    { label: "FAQs",               to: "/faq" },
    { label: "Booking Policy",     to: "/booking-policy" },
    { label: "Cancellation Policy",to: "/cancellation-policy" },
  ]},
  { title: "Retreats", links: [
    { label: "Tarzan Community", to: "/retreats/community" },
    { label: "Tarzan Farmstay",  to: "/retreats/farmstay" },
    { label: "Tarzan Villa",     to: "/retreats/villa" },
    { label: "Tarzan Village",   to: "/retreats/village" },
  ]},
];

export default function Footer() {
  return (
    <footer className="bg-forest-900 text-ivory/85 mt-24 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.07]" style={{backgroundImage:"radial-gradient(circle at 20% 20%, #6CA439 0, transparent 40%), radial-gradient(circle at 85% 70%, #6CA439 0, transparent 35%)"}} />
      <div className="container-tarzan relative pt-20 pb-10">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10">
          <div className="col-span-2">
            <img src={LOGO_WHITE} alt="Tarzan Nature Retreat" className="h-16 w-auto -ml-2" />
            <p className="mt-5 text-ivory/70 max-w-xs leading-relaxed text-sm">
              A curated collection of India's most beautiful nature retreats — handpicked, hosted with love.
            </p>
            <div className="mt-6 space-y-2 text-sm">
              <a href="tel:+919491317777" className="flex items-center gap-2 text-ivory hover:text-sand-200 transition-colors">
                <Phone className="h-4 w-4 text-forest-400" /> +91 94913 17777
              </a>
              <div className="flex items-start gap-2 text-ivory/70 leading-relaxed">
                <MapPin className="h-4 w-4 text-forest-400 mt-0.5 shrink-0" />
                <span>Tarzan Hyderabad Retreat<br/>Nature and Home, opposite Anganwadi center,<br/>Amdapur, Telangana 501504</span>
              </div>
              <div className="pt-2">
                <span className="inline-flex items-center gap-2 bg-ivory/10 border border-ivory/15 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-semibold text-sand-200">
                  <span className="h-1.5 w-1.5 rounded-full bg-forest-400" /> Now bookable in Hyderabad · More locations coming soon
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-7">
              {SOCIALS.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="h-10 w-10 rounded-full border border-ivory/20 grid place-items-center hover:bg-ivory hover:text-forest-900 transition-colors"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>

            {/* An EBG Group Company */}
            <a
              href="https://www.theebg.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-ebg"
              className="mt-7 inline-flex items-center gap-4 bg-ivory/5 border border-ivory/15 rounded-2xl pl-4 pr-6 py-3 hover:border-sand-200/60 hover:bg-ivory/10 transition-colors group"
            >
              <img
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_jungle-retreat-3/artifacts/n14kbwtm_EBG%20Logo.png"
                alt="EBG Group"
                loading="lazy"
                className="h-9 md:h-10 w-auto shrink-0"
              />
              <div className="border-l border-ivory/15 pl-4 leading-tight">
                <div className="text-ivory text-[13px] md:text-sm">An <span className="text-sand-200 font-medium">EBG</span> Group Company</div>
                <div className="text-[11px] text-ivory/55 mt-1 inline-flex items-center gap-1 group-hover:text-sand-200 transition-colors">
                  theebg.com <ArrowUpRight className="h-3 w-3" />
                </div>
              </div>
            </a>
          </div>
          {COLS.map((col) => (
            <div key={col.title}>
              <div className="text-ivory text-sm font-semibold tracking-wide uppercase">{col.title}</div>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}><Link to={l.to} className="text-ivory/70 hover:text-ivory text-sm transition-colors">{l.label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 pt-10 border-t border-ivory/10" data-testid="footer-sdg">
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr_auto] gap-8 lg:gap-10 items-center">
            {/* Master SDG logo */}
            <div className="flex lg:block justify-center lg:justify-start">
              <div className="bg-ivory rounded-2xl p-4 sm:p-5 ring-1 ring-ivory/15 shrink-0">
                <img
                  src="https://www.un.org/sustainabledevelopment/wp-content/uploads/2019/08/E_SDG_logo_UN_emblem_square_trans_WEB-400x343.png"
                  alt="United Nations Sustainable Development Goals"
                  loading="lazy"
                  className="block w-[120px] sm:w-[130px] lg:w-[140px] h-auto"
                />
              </div>
            </div>

            <div className="max-w-md text-center lg:text-left">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-sand-200 font-semibold">
                <span className="h-1.5 w-1.5 rounded-full bg-sand-200" /> Our Commitment
              </div>
              <h3 className="font-serif text-2xl md:text-[28px] text-ivory leading-tight mt-3">
                Aligned with the United Nations<br className="hidden sm:block" />
                <em className="not-italic text-sand-200"> Sustainable Development Goals.</em>
              </h3>
              <p className="text-ivory/65 text-sm mt-3 leading-relaxed">
                Every Tarzan retreat is designed, built and run to advance these four goals — through local sourcing, low-impact construction, forest stewardship and community partnerships.
              </p>
            </div>

            <div className="grid grid-cols-4 gap-3 sm:gap-4 lg:gap-4 justify-items-center lg:justify-items-end">
              {[
                { n: 11, label: "Sustainable Cities and Communities" },
                { n: 12, label: "Responsible Consumption and Production" },
                { n: 13, label: "Climate Action" },
                { n: 15, label: "Life on Land" },
              ].map(({n, label}) => (
                <a
                  key={n}
                  href={`https://sdgs.un.org/goals/goal${n}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`SDG ${n} — ${label}`}
                  aria-label={`UN Sustainable Development Goal ${n}: ${label}`}
                  data-testid={`footer-sdg-${n}`}
                  className="group block"
                >
                  <div className="aspect-square w-[64px] sm:w-[76px] md:w-[88px] lg:w-[96px] rounded-xl overflow-hidden bg-ivory/5 ring-1 ring-ivory/10 group-hover:ring-ivory/40 group-hover:-translate-y-1 transition-all">
                    <img
                      src={`https://sdgs.un.org/sites/default/files/goals/E_SDG_Icons-${n}.jpg`}
                      alt={`UN SDG ${n}: ${label}`}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </a>
              ))}
            </div>
          </div>
          <p className="text-[10px] text-ivory/40 text-center lg:text-left mt-5 leading-relaxed max-w-2xl mx-auto lg:mx-0">
            The SDG icons are used in accordance with the United Nations' SDG branding guidelines. The content of this site has not been approved by the United Nations.
          </p>
        </div>

        <div className="mt-12 pt-8 border-t border-ivory/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-xs text-ivory/55 tracking-wide">© {new Date().getFullYear()} Tarzan Nature Retreat. All rights reserved.</div>
          <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-5 text-xs text-ivory/55">
            <div className="flex items-center gap-2">
              <Leaf className="h-3.5 w-3.5 text-forest-400" /> Made with intention for nature lovers.
            </div>
            <span className="hidden md:inline text-ivory/20">·</span>
            <div>
              Design &amp; Development by{" "}
              <a
                href="https://www.reachvel.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-credit-reachvel"
                className="text-ivory hover:text-sand-200 underline-offset-4 hover:underline transition-colors font-medium"
              >
                Reachvel
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
